import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';
import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import {
  COLUMN_WIDTHS,
  pinned,
  useTextColumnWidth,
  WRAP_ANYWHERE
} from '../../../../ui/components/pure/list-table/list-table-column-styles';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { Colors } from '../../../../ui/theme/colors';
import { useHasChildProjects } from '../../../../hooks/use-has-child-projects.hook';
import { useMediaQuery, useTheme } from '@mui/material';
import { Delivery } from '../../models/delivery.model';
import { MultipleNotificationColumn } from '../../../notifications/components/multiple-notification-column';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useHasMultipleHubs } from '../../../lockers/hubs.queries';
import { decodeBarcode } from '../../../../ui/util/barcode';
import { getDeliveryStateLabelForProject } from '../../../../core/util/status-label.util';
import { useBookingFlowType } from '../../../orders/list/actions/book-order/use-booking-flow-type.hook';

const FIXED_COLUMN_WIDTHS = {
  ...COLUMN_WIDTHS,
  locker: '120px',
  collectionCode: '130px'
} as const;

function shouldShowColumn(key: string, visibleColumns?: { key: string; label: string }[]) {
  return !visibleColumns || visibleColumns.some((col) => col.key.toUpperCase() === key.toUpperCase());
}

// signature
export function useDeliveriesTable({
  projectID,
  hideAgentColumn,
  visibleColumns
}: {
  projectID: string;
  hideAgentColumn: boolean;
  visibleColumns?: { key: string; label: string }[];
}) {
  const hasChildProjects = useHasChildProjects(projectID);
  const hasMultipleHubs = useHasMultipleHubs(projectID);
  const featureCheckResult = useHasUIFeatures(['internal-groundfloor']);
  const bookingFlowType = useBookingFlowType(projectID);

  const theme = useTheme();
  const isMoreThan1600Px = useMediaQuery(theme.breakpoints.up(1600));
  const textWidth = useTextColumnWidth();

  const tb = initTable<Delivery>();
  tb.styles({
    table: { tableLayout: 'fixed' }
  });
  tb.rowStyles({ td: { height: '64px' } });

  // Dates are pinned only when an unpinned text column is there to absorb the slack: with every
  // column pinned, the browser would spread it across the pinned ones too.
  const isFreeTextColumnShown =
    ['project', 'hub', 'to', 'barcode', 'unit'].some((key) => shouldShowColumn(key, visibleColumns)) ||
    (featureCheckResult.hasAllFeatures && shouldShowColumn('agent', visibleColumns));
  // --- Desktop Columns ---
  if (shouldShowColumn('project', visibleColumns)) {
    tb.column('PROJECT', TableComponents.Text, { value: ':project.name' }).options({
      isSortable: false,
      minBreakpoint: 'md',
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('hub', visibleColumns)) {
    tb.column('HUB', TableComponents.Text, { value: ':deliveredAtHub.name' }).options({
      isSortable: true,
      sortId: 'deliveredAtHub.name',
      minBreakpoint: 'md',
      tooltip: 'hub name',
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('type', visibleColumns)) {
    tb.column('TYPE', TableComponents.SVGCell, {
      SVG: (row) => row.deliveryTypeIcon,
      iconSize: `~24px`
    }).options({
      isSortable: true,
      sortId: 'deliveryType',
      minBreakpoint: 'md',
      tooltip: 'delivery type',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.icon)
    });
  }

  if (shouldShowColumn('by', visibleColumns)) {
    tb.column('BY', TableComponents.TenantLogo, {
      name: ':deliveredByLabel',
      imageURL: ':deliveredBy.party.imageURL'
    }).options({
      isSortable: true,
      sortId: 'deliveredBy.party.name',
      minBreakpoint: 'md',
      headerStyles: pinned(textWidth(FIXED_COLUMN_WIDTHS.by, 'logoOrLabel')),
      styles: WRAP_ANYWHERE
    });
  }

  if (featureCheckResult.hasAllFeatures && shouldShowColumn('agent', visibleColumns)) {
    tb.column('AGENT', TableComponents.Text, {
      value: ':deliveredByAgentLabel'
    }).options({
      isSortable: true,
      sortId: 'deliveredBy.agent.name',
      minBreakpoint: 'md',
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('deliveryTime', visibleColumns)) {
    tb.column('DELIVERY STARTED TIME', TableComponents.DateTime, {
      value: ':createdTime'
    }).options({
      isSortable: true,
      sortId: 'createdTime',
      minBreakpoint: 'md',
      tooltip: 'delivery started time',
      headerStyles: isFreeTextColumnShown ? pinned(FIXED_COLUMN_WIDTHS.dateTime) : undefined
    });
  }

  if (shouldShowColumn('to', visibleColumns)) {
    tb.column('TO', TableComponents.BoldText, {
      value: ':deliveredTo.party.name'
    }).options({
      isSortable: true,
      sortId: 'deliveredTo.party.name',
      minBreakpoint: 'md',
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('barcode', visibleColumns)) {
    tb.column('BARCODE', TableComponents.Text, {
      value: (row) => decodeBarcode(row.barcode)
    }).options({
      isSortable: false,
      minBreakpoint: 'md',
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('unit', visibleColumns)) {
    tb.column('UNIT', TableComponents.Text, {
      value: ':unitLabel'
    }).options({
      isSortable: true,
      sortId: 'deliveredTo.parent.name',
      minBreakpoint: 'md',
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('lockerName', visibleColumns)) {
    tb.column('LOCKER NAME', TableComponents.Text, {
      value: (row: Delivery) => row.lockerName || '—'
    }).options({
      isSortable: false,
      minBreakpoint: 'md',
      tooltip: 'locker name',
      headerStyles: pinned(textWidth(FIXED_COLUMN_WIDTHS.locker, 'logoOrLabel')),
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('lockerSize', visibleColumns)) {
    tb.column('LOCKER SIZE', TableComponents.Text, {
      value: (row: Delivery) => row.lockerSize || '—'
    }).options({
      isSortable: false,
      minBreakpoint: 'md',
      tooltip: 'locker size',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.locker)
    });
  }

  if (shouldShowColumn('completedTime', visibleColumns)) {
    tb.column('COMPLETED TIME', TableComponents.DateTime, {
      value: ':completedTime'
    }).options({
      isSortable: true,
      sortId: 'completedTime',
      minBreakpoint: 'md',
      headerStyles: isFreeTextColumnShown ? pinned(FIXED_COLUMN_WIDTHS.dateTime) : undefined
    });
  }

  if (shouldShowColumn('collectionCode', visibleColumns)) {
    tb.column('COLLECTION CODE', TableComponents.Text, {
      value: ':collectionCode'
    }).options({
      isSortable: false,
      minBreakpoint: 'md',
      tooltip: 'code used during parcel collection',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.collectionCode),
      styles: WRAP_ANYWHERE
    });
  }

  if (shouldShowColumn('channel', visibleColumns)) {
    tb.column('CHANNEL', TableComponents.SVGCell, {
      SVG: ':deliveryChannelIcon',
      colorInput: `~${Colors.timelineGray}`,
      iconSize: `~24px`
    }).options({
      isSortable: true,
      sortId: 'deliveryChannel',
      minBreakpoint: 'md',
      tooltip: 'delivery channel',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.icon)
    });
  }

  if (shouldShowColumn('status', visibleColumns)) {
    tb.column('STATUS', TableComponents.WorkflowStatus, {
      label: (row) => {
        return getDeliveryStateLabelForProject(row.state, bookingFlowType);
      },
      color: ':stateColor'
    }).options({
      isSortable: true,
      sortId: 'state',
      minBreakpoint: 'md',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.status),
      tooltip: 'delivery status'
    });
  }

  if (shouldShowColumn('notifications', visibleColumns)) {
    tb.column(
      {
        name: 'NOTIFICATIONS',
        render: () => (
          <div>
            <NotificationsNoneIcon />
          </div>
        )
      },
      MultipleNotificationColumn,
      {
        notifications(row) {
          return row.notifications;
        },
        align: '~left',
        iconSize: `~24px`,
        maxVisibleNotifications: '~1'
      }
    ).options({
      minBreakpoint: 'md',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.notifications)
    });
  }

  // --- Mobile Columns ---
  tb.columnGroup('TIME DELIVERED')
    .row(TableComponents.DateTime, { value: ':deliveredTime' })
    .row(TableComponents.BoldText, { value: ':deliveredTo.party.name' })
    .row(TableComponents.Text, { value: ':unitLabel' })
    .options({
      isSortable: true,
      sortId: 'deliveredTime',
      maxBreakpoint: 'sm',
      tooltip: 'delivered time'
    });

  tb.columnGroup('')
    .row(TableComponents.WorkflowStatus, {
      label: (row) => {
        return getDeliveryStateLabelForProject(row.state, bookingFlowType);
      },
      color: ':stateColor',
      align: '~centre'
    })
    .row(TableComponents.Text, { value: ':deliveredInto.hub.name' }, () => hasMultipleHubs)
    .row(
      TableComponents.TenantLogo,
      {
        name: ':project.name',
        imageURL: ':project.imageURL',
        align: '~right'
      },
      () => hasChildProjects
    )
    .options({
      isSortable: false,
      maxBreakpoint: 'sm',
      styles: { display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }
    });

  // --- Predicates ---
  tb.addPredicate('PROJECT', () => hasChildProjects);
  tb.addPredicate('UNIT', () => isMoreThan1600Px);
  tb.addPredicate('CHANNEL', () => isMoreThan1600Px);
  tb.addPredicate('AGENT', () => !hideAgentColumn);

  return tb;
}
